import React, { useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaInstagram } from 'react-icons/fa';
import { AiOutlineMail, AiFillPhone } from 'react-icons/ai';
import { obtenerEmpresa } from '../../scripts/obtenerEmpresa';
import acuerdo from '../../images/acuerdo.png';
import transfer from '../../images/transfer.png';
import mercadopago from '../../images/mercadopago.png';
import moto from '../../images/moto.png';
import whatsapp from '../../images/whatsapp.png';
import logo from '../../images/logo.svg';

const FooterContainer = styled.footer`
	${tw`relative bg-[#68a38a] text-gray-100 py-12 mt-20`}
`;
const Content = tw.div`max-w-screen-xl mx-auto px-8`;
const FlexContainer = tw.div`flex flex-col lg:flex-row justify-between items-center lg:items-start gap-12`;
const Column = tw.div`flex flex-col items-center lg:items-start text-center lg:text-left`;

const LogoColumn = styled(Column)`
	${tw`flex-1`}
`;

const LogoContainer = tw.div`flex items-center justify-center lg:justify-start`;
const LogoImg = styled.img`
	${tw`rounded-full`}
	width: 80px;
	height: 80px;
`;
const LogoText = tw.h3`ml-3 text-2xl font-bold text-white`;
const Tagline = tw.p`text-sm text-gray-300 mt-2 italic pl-20`;

const ColumnTitle = tw.h5`uppercase font-bold mb-4 text-white text-lg border-b-2 border-[#99cdb6] pb-2 w-full lg:w-auto`;
const LinkList = tw.ul`space-y-3 text-sm`;
const LinkItem = tw.li`hover:text-[#99cdb6] cursor-pointer transition-all duration-300 transform hover:translate-x-1`;

const ContactInfo = tw.div`space-y-3 text-sm`;
const ContactItem = tw.div`flex items-center justify-center lg:justify-start space-x-2 hover:text-[#99cdb6] transition-colors duration-300`;
const Icon = tw.div`text-xl text-[#99cdb6]`;

const PaymentShipping = tw.div`flex justify-center lg:justify-start flex-wrap gap-3 mt-3`;

const IconButton = styled.div`
	${tw`w-16 h-16 p-2 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-105`}
	background-color: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(4px);

	&:hover {
		background-color: rgba(255, 255, 255, 0.3);
	}

	svg {
		${tw`w-8 h-8`}
	}
	img {
		${tw`object-contain flex-shrink-0`}
		max-width: 100%;
		max-height: 100%;
	}
`;

const SocialLink = styled.a`
	${tw`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 transform hover:scale-110`}
	background-color: rgba(255, 255, 255, 0.2);

	&:hover {
		background-color: #99cdb6;
	}

	svg {
		${tw`w-5 h-5 text-white`}
	}
`;

const Divider = tw.div`border-t border-gray-800 border-opacity-25 my-8`;
const Copywright = tw.div`text-center text-gray-300 text-sm`;

const Footer = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const currentYear = new Date().getFullYear();
	const empresa = useSelector((state) => state.empresa || {});
	const userInfo = useSelector((state) => state.usuario.userInfo);
	const viandasState = useSelector((state) => state.viandas || { items: [], status: 'idle' });
	const mercadoPagoHabilitado = empresa?.mercado_pago_habilitado !== false;
	const esAdministrador = userInfo?.rol === 'Administrador' || Number(userInfo?.rol_id) === 1;
	const puedeVerViandas = esAdministrador || (viandasState.status === 'succeeded' && viandasState.items.length > 0);

	useEffect(() => {
		const fetchEmpresa = async () => {
			try {
				await obtenerEmpresa(dispatch, sessionStorage.getItem('token'));
			} catch (e) {
				console.log(e);
			}
		};
		fetchEmpresa();
	}, [dispatch]);

	return (
		<FooterContainer>
			<Content>
				<FlexContainer>
					{/* LOGO Y DESCRIPCIÓN */}
					<LogoColumn>
						<LogoContainer>
							<LogoImg src={logo} alt="La Cocina de Emilia" />
							<LogoText>La Cocina de Emilia</LogoText>
						</LogoContainer>
						<Tagline>"Comida casera hecha con amor"</Tagline>
					</LogoColumn>

					{/* TIENDA */}
					<Column>
						<ColumnTitle>Navegación</ColumnTitle>
						<LinkList>
							<LinkItem onClick={() => navigate('/')}>Inicio</LinkItem>
							<LinkItem onClick={() => navigate('/productos')}>Productos</LinkItem>
							{puedeVerViandas && <LinkItem onClick={() => navigate('/viandas')}>Viandas</LinkItem>}
							<LinkItem onClick={() => navigate('/nosotros')}>Nosotras</LinkItem>
							<LinkItem onClick={() => navigate('/envios')}>Envíos</LinkItem>
							<LinkItem onClick={() => navigate('/contacto')}>Contacto</LinkItem>
						</LinkList>
					</Column>

					{/* CONTACTO */}
					<Column>
						<ColumnTitle>Contacto</ColumnTitle>
						<ContactInfo>
							<ContactItem>
								<Icon>
									<AiFillPhone />
								</Icon>
								<span>{empresa.telefono || '098576439'}</span>
							</ContactItem>
							<ContactItem>
								<Icon>
									<AiOutlineMail />
								</Icon>
								<span>{empresa.correo || 'lacocinadeemilia65@gmail.com'}</span>
							</ContactItem>
							<ContactItem>
								<Icon>
									<FaInstagram />
								</Icon>
								<a
									href="https://www.instagram.com/la.cocinadeemilia/"
									target="_blank"
									rel="noopener noreferrer"
									tw="hover:underline"
								>
									@la.cocinadeemilia
								</a>
							</ContactItem>
						</ContactInfo>
					</Column>

					{/* MÉTODOS DE PAGO Y ENVÍO */}
					<Column>
						<ColumnTitle>Pagos y Envíos</ColumnTitle>
						<div tw="text-xs text-gray-300 mb-2">Métodos de pago</div>
						<PaymentShipping>
							<IconButton title="Efectivo">
								<img src={acuerdo} alt="Efectivo" />
							</IconButton>
							<IconButton title="Transferencia">
								<img src={transfer} alt="Transferencia" />
							</IconButton>
							{mercadoPagoHabilitado && (
								<IconButton title="Mercado Pago">
									<img src={mercadopago} alt="Mercado Pago" />
								</IconButton>
							)}
						</PaymentShipping>

						<div tw="text-xs text-gray-300 mb-2 mt-4">Métodos de envío</div>
						<PaymentShipping>
							<IconButton title="WhatsApp">
								<img src={whatsapp} alt="WhatsApp" />
							</IconButton>
							<IconButton title="Envío a domicilio">
								<img src={moto} alt="Envío" />
							</IconButton>
						</PaymentShipping>
					</Column>
				</FlexContainer>

				{/* DIVIDER Y COPYRIGHT */}
				<Divider />
				<Copywright>&copy; {currentYear} La Cocina de Emilia. Todos los derechos reservados.</Copywright>
			</Content>
		</FooterContainer>
	);
};

export default Footer;
